import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api, useApi } from '../api/queries'
import type { BanReplay } from '../api/types'
import {
  describe,
  EMPTY,
  FIELDS,
  HISTORY,
  listSaved,
  parseScale,
  problem,
  readScale,
  removeSaved,
  saveScale,
  scaleSearch,
  serialiseScale,
  writeScale,
  type Field,
  type Scale,
} from '../lib/scales'
import { UniverseConstructorPage, UniverseDriverPage } from './PeoplePages'
import { UniverseRacePage } from './RacePage'
import {
  ReplayChampionshipPage,
  ReplayConstructorsPage,
  ReplayDriversPage,
  ReplaySeasonPage,
  universeOf,
  type Source,
} from './ReplayPages'

const TODAY: Scale = {
  ...EMPTY,
  race: '25, 18, 15, 12, 10, 8, 6, 4, 2, 1',
  sprint: '8, 7, 6, 5, 4, 3, 2, 1',
}

const HINTS: Record<Field, string> = {
  race: 'Points for 1st, 2nd, 3rd…',
  sprint: 'Points for 1st, 2nd, 3rd…',
  qualifying: 'By qualifying position, every season',
  practice: 'Each session, FP1 to FP4, from 1986',
  fastest_lap: 'Points for the fastest lap of the race',
  final: 'The last round of each season, multiplied: 2 doubles it',
}

/** The creation screen: a table typed in, kept in the URL once it is valid. */
export function ScaleEditPage() {
  const [params, setParams] = useSearchParams()
  // A draft, because the URL holds the tidy form: writing every keystroke
  // through it would swallow a comma the moment it was typed.
  const [draft, setDraft] = useState(() => readScale(params))
  const [name, setName] = useState('')
  const [saved, setSaved] = useState(listSaved)
  const [refused, setRefused] = useState(false)
  const wrong = problem(draft)
  const current = serialiseScale(draft)

  const use = (scale: Scale) => {
    setDraft(scale)
    if (!problem(scale)) setParams(writeScale(params, scale), { replace: true })
  }
  const keep = (ok: boolean) => {
    setRefused(!ok)
    setSaved(listSaved())
  }

  return (
    <section>
      <h1>Make a points table</h1>
      <p className="prose muted">
        Give points for races, sprints, qualifying, free practice and the fastest lap, and every
        championship is decided again on your table. Every result counts, both cars score for
        their team, and every round is worth full points unless you multiply the last.
      </p>
      <p className="scale__start">
        <span className="muted">Start from </span>
        <button type="button" className="link-button" onClick={() => use(TODAY)}>
          today’s table
        </button>{' '}
        <span className="muted">or</span>{' '}
        <button type="button" className="link-button" onClick={() => setDraft(EMPTY)}>
          a blank one
        </button>
      </p>
      <div className="scale">
        {FIELDS.map(({ key, label }) => (
          <div key={key} className="scale__field">
            <label className="scale__label" htmlFor={`scale-${key}`}>
              {label}
            </label>
            <input
              id={`scale-${key}`}
              type="text"
              inputMode="decimal"
              value={draft[key]}
              placeholder={key === 'fastest_lap' ? '0' : key === 'final' ? '1' : 'None'}
              aria-describedby={`scale-${key}-hint`}
              onChange={(event) => use({ ...draft, [key]: event.target.value })}
            />
            <span id={`scale-${key}-hint`} className="muted scale__hint">
              {HINTS[key]}
            </span>
          </div>
        ))}
      </div>
      {wrong ? (
        <p className="scale__problem" role="alert">
          {wrong}
        </p>
      ) : (
        <p className="prose">
          <Link to={`/points/championship${scaleSearch(params)}`}>See the champions →</Link>
        </p>
      )}

      <h2>Saved tables</h2>
      <form
        className="search"
        onSubmit={(event) => {
          event.preventDefault()
          keep(saveScale(name.trim(), draft))
          setName('')
        }}
      >
        <input
          type="text"
          value={name}
          aria-label="Name this table"
          placeholder="Name this table"
          onChange={(event) => setName(event.target.value)}
        />
        <button type="submit" disabled={Boolean(wrong) || !name.trim()}>
          Save
        </button>
      </form>
      {refused ? (
        <p className="scale__problem" role="alert">
          This browser would not store it.
        </p>
      ) : null}
      <ul className="saved">
        {[
          ...HISTORY.map(([title, text]) => ({ title, text, own: false })),
          ...Object.entries(saved)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([title, text]) => ({ title, text, own: true })),
        ].map(({ title, text, own }) => (
          <li key={`${own}:${title}`}>
            <button
              type="button"
              className="link-button"
              aria-current={text === current}
              onClick={() => use(parseScale(text))}
            >
              {title}
            </button>{' '}
            <span className="muted">{describe(parseScale(text))}</span>{' '}
            {own ? (
              <button
                type="button"
                className="link-button"
                aria-label={`Delete ${title}`}
                onClick={() => keep(removeSaved(title))}
              >
                Delete
              </button>
            ) : null}
          </li>
        ))}
      </ul>
      {Object.keys(saved).length ? null : (
        <p className="muted">None of your own yet. They are kept in this browser only.</p>
      )}
    </section>
  )
}

const SCALES: Source = {
  path: '/points',
  suffix: 'on your points',
  use: function useScaleSource() {
    const [params] = useSearchParams()
    const scale = readScale(params)
    const text = serialiseScale(scale)
    const chosen = Boolean(text) && !problem(scale)
    const search = scaleSearch(params)
    const replay = useApi<BanReplay>(
      ['scale', text],
      () => api.GET('/api/v1/scale', { params: { query: { scale: text } } }),
      { enabled: chosen },
    )
    return {
      chosen,
      search,
      replay,
      summary: chosen ? (
        <p className="prose">
          <span className="muted">Points: </span>
          {describe(scale)}. <Link to={`/points${search}`}>Change</Link>
        </p>
      ) : (
        <p className="prose">
          No points table yet. <Link to="/points">Make one →</Link>
        </p>
      ),
      seasonQuery: () => ({ scale: text }),
      driverSeasons: (slug) => [
        ['scale', 'driver-seasons', slug, text],
        () =>
          api.GET('/api/v1/scale/drivers/{slug}/seasons', {
            params: { path: { slug }, query: { scale: text } },
          }),
      ],
    }
  },
}

export const ScaleChampionshipPage = () => <ReplayChampionshipPage source={SCALES} />
export const ScaleDriversPage = () => <ReplayDriversPage source={SCALES} />
export const ScaleConstructorsPage = () => <ReplayConstructorsPage source={SCALES} />
export const ScaleSeasonPage = () => <ReplaySeasonPage source={SCALES} />

const SCALES_UNIVERSE = universeOf(SCALES)
export const ScaleDriverPage = () => <UniverseDriverPage universe={SCALES_UNIVERSE} />
export const ScaleConstructorPage = () => <UniverseConstructorPage universe={SCALES_UNIVERSE} />
export const ScaleRacePage = () => <UniverseRacePage universe={SCALES_UNIVERSE} />
